import React, { useState, useEffect, useCallback } from 'react'
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core'

import { Dialog, ImageUploadField, snackbar } from 'components'
import { useCheckFormErrors, useApiCall } from 'hooks'
import { ERRORS } from 'consts'
import { ApiError } from 'errors'
import { resizeFile } from 'utils'

import { updateStore, getActiveTags } from '../api'
import { UpdateStoreData, Store } from '../types'

const rules = {
  name: [
    { validate: (val: string) => val.trim().length > 0, message: 'El nombre es requerido' },
    {
      validate: (val: string) => val.length >= 5,
      message: 'El nombre debe tener al menos 5 caracteres',
    },
  ],
  secondaryName: [
    {
      validate: (val: string) => val.trim().length > 0,
      message: 'El nombre secundario es requerido',
    },
    {
      validate: (val: string) => val.length >= 5,
      message: 'El nombre secundario debe tener al menos 5 caracteres',
    },
  ],
  description: [
    {
      validate: (val: string) => val.length <= 20,
      message: 'La descripción no debe superar los 20 caracteres',
    },
  ],
  rut: [
    { validate: (val: string) => val.trim().length > 0, message: 'El RUT es requerido' },
    { validate: (val: string) => /^\d+$/.test(val), message: 'El RUT debe ser numérico' },
    {
      validate: (val: string) => val.length >= 5,
      message: 'El RUT debe tener al menos 5 caracteres',
    },
    {
      validate: (val: string) => val.length <= 40,
      message: 'El RUT no debe superar los 40 caracteres',
    },
  ],
  tagIds: [
    {
      validate: (tags: string[]) => tags.length > 0,
      message: 'Debe seleccionar al menos una etiqueta',
    },
  ],
}

interface UpdateStoreDialogProps {
  store: Store
  handleClose: () => void
  onDone: (updatedStore: Store) => void
}

const UpdateStoreDialog: React.FC<UpdateStoreDialogProps> = ({ store, handleClose, onDone }) => {
  // Estado del formulario
  const [name, setName] = useState(store.name)
  const [secondaryName, setSecondaryName] = useState(store.secondaryName || '')
  const [description, setDescription] = useState(store.description || '')
  const [usdEnabled, setUsdEnabled] = useState(store.usdEnabled)
  const [pointsEnabled, setPointsEnabled] = useState(store.pointsEnabled !== false)
  const [active, setActive] = useState(store.active)
  const [rut, setRut] = useState(store.rut)
  const [serie, setSerie] = useState(store.serie || '')
  const [tagIds, setTagIds] = useState<string[]>(store.tags?.map(tag => tag.id.toString()) || [])

  // Manejo de imágenes
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string>(
    typeof store.logoUrl === 'string' ? store.logoUrl : '',
  )
  const [storeImageFile, setStoreImageFile] = useState<File | null>(null)
  const [storeImagePreview, setStoreImagePreview] = useState<string>(
    typeof store.storeImageUrl === 'string' ? store.storeImageUrl : '',
  )

  // Carga de etiquetas
  const [tags, setTags] = useState<{ id: number; title: string }[]>([])
  const getTagsRequest = useCallback(() => getActiveTags(), [])
  const [fetchTags] = useApiCall<void, { tags: { id: number; title: string }[] }>(
    getTagsRequest,
  )

  useEffect(() => {
    const loadTags = async () => {
      try {
        const response = await fetchTags()
        setTags(response.tags)
      } catch (err) {
        console.error('Error al obtener tags', err)
      }
    }
    loadTags()
  }, [fetchTags])

  // Validación del formulario
  const data = { name, secondaryName, rut, tagIds, logoFile, storeImageFile, description }
  const { isAnyFieldEmpty, hasErrors, errors } = useCheckFormErrors(data, rules)
  const isSubmitDisabled = isAnyFieldEmpty || hasErrors

  // API Call para actualizar la tienda
  const [updateStoreApi, isLoading] = useApiCall<UpdateStoreData, Store>(updateStore)

  const handleSubmit = async () => {
    try {
      const storeData: UpdateStoreData = {
        id: store.id,
        name,
        secondaryName,
        description,
        usdEnabled,
        pointsEnabled,
        active,
        rut,
        serie: serie.trim(),
        tagIds: tagIds.join(','), // ✅ Convertimos el array en un string separado por comas
        logoUrl: logoFile ?? undefined,
        storeImageUrl: storeImageFile ?? undefined,
      }

      const updatedStore = await updateStoreApi(storeData)
      onDone(updatedStore)
    } catch (err) {
      console.error(err)
      const message = err instanceof ApiError ? err.message : ERRORS.GENERIC_ERROR_MESSAGE
      const variant = err instanceof ApiError && err.status < 400 ? 'success' : 'error'
      snackbar.show(message, variant)
    }
  }

  const handleTagChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const { value } = event.target
    setTagIds(Array.isArray(value) ? value.map(String) : []) // ✅ Convertimos a string
  }

  const handleFileChange = async (file: File, type: 'logo' | 'storeImage') => {
    const previewUrl = URL.createObjectURL(file) // 🔹 Convertir `File` en URL

    if (type === 'logo') {
      setLogoPreview(previewUrl)
      const image = await resizeFile(file, 192, 192)
      setLogoFile(image)
    } else {
      setStoreImagePreview(previewUrl)
      const image = await resizeFile(file, 600, 600)
      setStoreImageFile(image)
    }
  }

  const handleRemoveImage = (type: 'logo' | 'storeImage') => {
    if (type === 'logo') {
      setLogoFile(null)
      setLogoPreview('')
    } else {
      setStoreImageFile(null)
      setStoreImagePreview('')
    }
  }
  const handleUsdEnabledChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setUsdEnabled(e.target.checked)

  const handlePointsEnabledChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setPointsEnabled(e.target.checked)

  const handleActiveChange = (e: React.ChangeEvent<HTMLInputElement>) => setActive(e.target.checked)

  return (
    <Dialog
      title="Editar Marca"
      isOpen
      onCancel={handleClose}
      showActions
      okButtonText="Actualizar Marca"
      okButtonProps={{ disabled: isSubmitDisabled }}
      onAccept={handleSubmit}
      isLoading={isLoading}
      contentStyle={{ minWidth: 500, overflowY: 'scroll' }}
      overflow
      style={{ minHeight: '50%' }}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Razón social"
            required
            value={name}
            onChange={e => setName(e.target.value)}
            error={errors.name?.hasError}
            helperText={errors.name?.message}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Nombre secundario"
            required
            value={secondaryName}
            onChange={e => setSecondaryName(e.target.value)}
            error={errors.secondaryName?.hasError}
            helperText={errors.secondaryName?.message}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Descripción"
            required
            value={description}
            onChange={e => setDescription(e.target.value)}
            error={errors.description?.hasError}
            helperText={errors.description?.message}
          />
        </Grid>

        <Grid item xs={6}>
          <FormControlLabel
            control={<Checkbox checked={usdEnabled} onChange={handleUsdEnabledChange} />}
            label="Habilitar USD"
          />
        </Grid>
        <Grid item xs={6}>
          <FormControlLabel
            control={<Checkbox checked={active} onChange={handleActiveChange} />}
            label="Activo"
          />
        </Grid>
        <Grid item xs={6}>
          <FormControlLabel
            control={<Checkbox checked={pointsEnabled} onChange={handlePointsEnabledChange} />}
            label="Suma puntos"
          />
        </Grid>

        <Grid item xs={8}>
          <TextField
            fullWidth
            label="RUT"
            required
            value={rut}
            onChange={e => setRut(e.target.value.replace(/\D/g, ''))}
            error={errors.rut?.hasError}
            helperText={errors.rut?.message}
          />
        </Grid>

        <Grid item xs={4}>
          <TextField
            fullWidth
            label="Serie"
            value={serie}
            onChange={e => setSerie(e.target.value.toUpperCase())}
            helperText="Solo si esta marca comparte RUT con otra"
          />
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="tags-label">Etiquetas</InputLabel>
            <Select labelId="tags-label" multiple value={tagIds} onChange={handleTagChange}>
              {tags.map(tag => (
                <MenuItem key={tag.id} value={tag.id.toString()}>
                  {tag.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={6}>
          <ImageUploadField
            id="logo-upload"
            label="Logo"
            preview={logoPreview}
            onChange={file => handleFileChange(file, 'logo')}
            onDelete={() => handleRemoveImage('logo')}
          />
        </Grid>

        <Grid item xs={6}>
          <ImageUploadField
            id="store-image-upload"
            label="Imagen de la tienda"
            variant="wide"
            preview={storeImagePreview}
            onChange={file => handleFileChange(file, 'storeImage')}
            onDelete={() => handleRemoveImage('storeImage')}
          />
        </Grid>
      </Grid>
    </Dialog>
  )
}

export { UpdateStoreDialog }
